import { useEffect, useState } from 'react';
import {
  addTrackingToExternalBookingUrl,
  getCoachPageUrl,
  getLessonJoinPageUrl,
} from 'constants/pages';
import { withEngagementSource } from 'constants/payloads/club/engagementBeacon';
import { EMPTY_AVATAR_SRC, getIsValidUsernameFormat } from 'constants/user';
import { trackEvent } from 'services/client/analytics';
import { trackCtaClick, trackView } from 'services/client/analytics/engagement';
import { CLINIC_JOIN_CLICKED } from 'services/client/analytics/events';
import { getActivityImageUrl } from 'utils/shared/getActivityImageUrl';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { nameToLastInitials } from 'utils/shared/name/nameToLastInitials';
import { formatVenueEventDateTime } from 'utils/shared/time/formatEventDateTime';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';
import CloseIcon from 'svg/CloseIcon';
import Directions from 'svg/Directions';
import Location from 'svg/Location';
import { getClinicLinkType } from 'screens/ExploreClinics/getClinicLinkType';
import AcitivityBadge from 'components/ActivityBadge';
import Button, { ButtonLink, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import { ClinicDetailModalProps } from '../props';

const ClinicDetailModal: React.FC<ClinicDetailModalProps> = ({ open, handleClose, clinic }) => {
  const [showAllParticipants, setShowAllParticipants] = useState(false);

  // Engagement: a clinic is a LESSON entity; this modal is its detail view. Opened
  // from the clinics marketplace, so attributed to BROWSE.
  useEffect(() => {
    if (open && clinic?.id) {
      trackView({ entityType: 'LESSON', entityId: clinic.id, source: 'BROWSE' });
    }
  }, [open, clinic?.id]);

  if (!clinic) return null;

  const handleJoinClick = () => {
    trackCtaClick({ entityType: 'LESSON', entityId: clinic.id, source: 'BROWSE' });

    // Track clinic join button click event
    const linkType = getClinicLinkType(clinic);
    trackEvent(CLINIC_JOIN_CLICKED, {
      clinic_id: clinic.id,
      clinic_title: clinic.title,
      link_type: linkType,
      source: 'modal',
      sport: clinic.sport,
      external_source: clinic.externalSource || null,
      has_external_booking_url: !!clinic.externalBookingUrl?.trim(),
      price_unit_amount: clinic.priceUnitAmount,
      participant_count: clinic.participants?.length || 0,
      participant_limit: clinic.participantLimit || 0,
      venue_name: clinic.venue?.title || clinic.userCustomCourt?.fullAddress || null,
      coach_name: clinic.ownerProfile?.fullName || null,
      start_date_time: clinic.startDateTime,
    });
  };

  const startDateTime = clinic?.startDateTime;
  const clinicDateTime = formatVenueEventDateTime(
    startDateTime,
    clinic?.endDateTime,
    resolveVenueTimeZone(clinic),
  );
  const lessonDate = clinicDateTime?.date ?? '';
  const lessonTime = clinicDateTime
    ? `${clinicDateTime.time}${clinicDateTime.timezoneLabel ? ` ${clinicDateTime.timezoneLabel}` : ''}`
    : '';
  const participantLimit = clinic?.participantLimit || 0;
  const participantCount = clinic?.participants.length;
  const spotsLeft = participantLimit - participantCount;

  // Initial view shows only 6 slots (participants + empty)
  const initialParticipantsToShow = 6;

  // Determine which participants to display based on view state
  const displayedParticipants = showAllParticipants
    ? clinic.participants
    : clinic.participants.slice(0, initialParticipantsToShow);

  // Calculate empty slots to display
  const emptySlots = showAllParticipants
    ? Array(spotsLeft).fill(0)
    : Array(Math.min(initialParticipantsToShow - displayedParticipants.length, spotsLeft)).fill(0);

  const price = clinic.priceUnitAmount
    ? convertUnitPriceToFormattedPrice(clinic.priceUnitAmount, clinic.currency).priceDisplay
    : 0;

  const toggleParticipantsView = () => {
    setShowAllParticipants(!showAllParticipants);
  };

  const isExternal = getClinicLinkType(clinic) === 'external';

  const coverImageSrc = getActivityImageUrl(clinic) || '';

  return (
    <Modal
      isOpen={open}
      handleClose={handleClose}
      className="flex max-h-[100dvh] flex-col sm:max-h-[calc(100dvh-2rem)]"
      classNameMaxWidth="max-w-[40rem]"
    >
      <div className="flex min-h-0 flex-1 flex-col max-sm:h-[100dvh]">
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 justify-end px-ds-3xl py-5 max-sm:px-ds-lg">
            <button onClick={handleClose} className="cursor-pointer">
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-ds-3xl pb-ds-3xl max-sm:px-ds-lg">
            <div className="flex gap-ds-xl max-sm:gap-1">
              <img
                src={coverImageSrc}
                alt={isExternal ? 'venue-image' : 'profile-logo'}
                className="h-[6.5rem] w-[6.5rem] shrink-0 rounded-full object-cover object-top max-sm:h-[6.5rem] max-sm:w-[6.5rem] max-sm:rounded-xl"
              />
              <div className="flex min-w-0 flex-1 justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1 sm:items-start">
                      <span className="max-sm:typography-product-chips-filters typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                        {lessonDate} <span className="text-color-brand-primary">&bull;</span>{' '}
                        {lessonTime}
                      </span>
                      <AcitivityBadge size="sm" sport={clinic.sport} type={clinic.type} />
                    </div>
                    {!!price && (
                      <span className="typography-product-heading max-sm:typography-product-subheading sm:hidden">
                        {price}
                      </span>
                    )}
                  </div>

                  <h2 className="max-sm:typography-product-heading-mobile typography-product-heading-compact-desktop mt-ds-sm">
                    {clinic.title}
                  </h2>

                  <div className="mt-3 flex items-start gap-4 max-sm:justify-between">
                    <div className="inline-flex min-w-0 gap-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary" />
                      <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile max-sm:truncate">
                        {clinic?.userCustomCourt?.fullAddress || clinic?.venue?.addressString}
                      </span>
                    </div>
                    <a
                      target="_blank"
                      href={`https://maps.google.com?q=${
                        clinic?.userCustomCourt?.fullAddress || clinic?.venue?.addressString
                      }`}
                      className="inline-flex items-center gap-1"
                    >
                      <Directions className="h-4 w-4 shrink-0" />
                      <span className="typography-product-button-label-small">Directions</span>
                    </a>
                  </div>
                </div>
                {!!price && (
                  <span className="typography-product-heading max-sm:typography-product-subheading max-sm:hidden">
                    {price}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-8 h-[1px] w-full shrink-0 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

            {!isExternal && (
              <div className="mt-8">
                <h3 className="typography-product-subheading mb-3">Coach</h3>
                <div className="flex items-center gap-3 max-sm:justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={clinic.ownerProfile?.profileImageUrl || ''}
                      alt="coach"
                      className="h-12 w-12 rounded-full border border-color-border-input-lightmode bg-gray-100 object-cover object-top dark:border-color-border-input-darkmode"
                    />
                    <div className="flex flex-col">
                      <span className="typography-product-card-title-desktop">
                        {clinic.ownerProfile?.fullName}
                      </span>
                      {getIsValidUsernameFormat(clinic.ownerProfile?.username || '') && (
                        <span className="typography-product-text-card-mobile mt-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                          @{clinic.ownerProfile?.username || ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <ButtonLink
                    href={withEngagementSource(
                      getCoachPageUrl(clinic.ownerProfile?.username || ''),
                      'BROWSE',
                    )}
                    variant="secondary"
                    size="sm"
                    className="ml-ds-2xl w-fit"
                  >
                    View coach
                  </ButtonLink>
                </div>
              </div>
            )}

            {!isExternal && (
              <div className="mt-8">
                <div className="mb-ds-md flex  items-center justify-between">
                  {!clinic.externalBookingUrl ? (
                    <h3 className="typography-product-subheading mb-2 sm:mb-0">
                      Players going {participantCount}/{participantLimit}
                    </h3>
                  ) : (
                    <div>&nbsp;</div>
                  )}

                  {participantCount + spotsLeft > initialParticipantsToShow && (
                    <ButtonText
                      size="md"
                      onClick={toggleParticipantsView}
                      className="flex items-center gap-1 self-start text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                    >
                      View {showAllParticipants ? 'Less' : 'All'}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          showAllParticipants ? 'rotate-180' : ''
                        }`}
                      />
                    </ButtonText>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-6">
                  {displayedParticipants.map((participant) => (
                    <div key={participant.id} className="flex flex-col items-center gap-1">
                      <div className="relative">
                        <img
                          src={getProfileImageUrlOrPlaceholder({
                            path: participant.userProfile?.profileImagePath || '',
                          })}
                          alt={participant.userProfile?.fullName || 'Player'}
                          className="h-10 w-10 rounded-full border border-color-border-input-lightmode bg-gray-100 object-cover object-top dark:border-color-border-input-darkmode sm:h-12 sm:w-12"
                        />
                        <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-[2px] border-white bg-color-brand-primary text-white sm:h-5 sm:w-5">
                          <CheckIcon className="h-2 w-2 sm:h-3 sm:w-3" />
                        </div>
                      </div>
                      <span className="typography-product-chips-filters text-center">
                        {nameToLastInitials(participant.userProfile?.fullName || 'Player')}
                      </span>
                    </div>
                  ))}

                  {emptySlots.map((_, index) => (
                    <div key={`empty-${index}`} className="flex flex-col items-center gap-1">
                      <img
                        className="h-10 w-10 rounded-full grayscale sm:h-12 sm:w-12"
                        src={EMPTY_AVATAR_SRC}
                        alt="Empty slot"
                      />
                      <span className="typography-product-chips-filters text-center text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                        open
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <h3 className="typography-product-subheading mb-2">Lesson plan</h3>
              <p className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                {clinic.description || 'No lesson plan available.'}
              </p>
            </div>

            <div className="mt-6">
              <h3 className="typography-product-subheading mb-2">Court location</h3>
              <div className="flex items-center justify-between">
                <p className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {clinic?.venue?.addressString || clinic?.userCustomCourt?.fullAddress}
                </p>
                <ButtonText
                  size="md"
                  className="inline-flex items-center gap-1"
                  onClick={() =>
                    window.open(
                      `https://maps.google.com?q=${
                        clinic?.venue?.addressString || clinic?.userCustomCourt?.fullAddress
                      }`,
                    )
                  }
                >
                  <Directions className="h-4 w-4" />
                  Directions
                </ButtonText>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-ds-lg border-t border-color-border-input-lightmode px-ds-3xl py-5 dark:border-color-border-input-darkmode max-sm:px-ds-lg">
            <ButtonLink
              variant="brand"
              sizeDesktop="lg"
              sizeMobile="md"
              className="ml-auto w-fit"
              href={
                !!clinic.externalBookingUrl?.trim()
                  ? addTrackingToExternalBookingUrl(clinic.externalBookingUrl, clinic.id)
                  : getLessonJoinPageUrl(clinic.id)
              }
              isExternal={!!clinic.externalBookingUrl}
              onClick={handleJoinClick}
            >
              {spotsLeft > 0 ? 'Join' : 'Fully Booked'}
            </ButtonLink>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ClinicDetailModal;
